import { useEffect, useRef, type ReactNode } from "react";
import { Button } from "@legends/ui";

// A lightweight admin-scoped modal (ADR 0160, Phase 4). Unlike the shared
// ConfirmDialog it does NOT re-focus a button whenever a handler identity
// changes, so a form field inside it keeps focus while you type (the shared
// dialog's focus effect steals focus on every keystroke, and a Space then
// activates Cancel). Escape + backdrop click cancel; focus lands on the first
// field once, on open.
export default function AdminModal({
  title,
  children,
  confirmLabel = "Save",
  cancelLabel = "Cancel",
  busy = false,
  confirmDisabled = false,
  destructive = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  children: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  confirmDisabled?: boolean;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);

  // Focus the first field once on open (empty deps — never steals focus later).
  useEffect(() => {
    bodyRef.current?.querySelector<HTMLElement>("input, select, textarea, button")?.focus();
  }, []);

  // Escape cancels (a ref keeps the handler current without re-binding).
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      role="presentation"
      onClick={() => onCancel()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        zIndex: 200,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          maxWidth: "100%",
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
        }}
      >
        <h2
          style={{
            margin: 0,
            padding: "18px 20px 8px",
            fontFamily: "var(--font-display)",
            fontSize: "var(--fs-title)",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "var(--ls-display)",
            color: destructive ? "var(--live)" : "var(--text-1)",
          }}
        >
          {title}
        </h2>
        <div ref={bodyRef} style={{ padding: "6px 20px 16px" }}>
          {children}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "12px 20px",
            borderTop: "1px solid var(--border)",
          }}
        >
          <Button variant="muted" disabled={busy} onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant="accent" disabled={busy || confirmDisabled} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
